import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { setTitle } from "../../../../helpers/MetaTag";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { postDelete, postList, postOrdering, postStatusChange } from "../../../../services/postService";
import Badge from "../../../../components/elements/Badge";
import { getPaginationRange, handleFilterChange } from "../../../../helpers";
import Table from "../../../../components/elements/table/Table";
import Button from "../../../../components/form/Button";
import DeleteModal from "../../../../components/common/DeleteModal";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import TableControls from "../../../../components/elements/TableControls";
import ActionBar from "../../../../components/elements/ActionBar";
import { status_filter } from "../../../../components/constant/constant";
import TagFilter from "../../../../components/elements/TagFilter";
import Tooltip from "../../../../components/elements/Tooltip";
import { useLoadTaxonomyList } from "../../../../helpers/GetCommonList";
import FilterTags from "../../../../components/elements/FilterTags";
import { t } from "i18next";
import { orderList } from "../../../../services/orderService";
import StatusChangeTooltip from "../../../../components/common/StatusChangeTooltip";
import ReactPortal from "../../../../components/common/ReactPortal";
import { transactionList } from "../../../../services/transactionService";
import { tableList } from "../../../../services/tableService";
import SearchableDropdown from "../../../../components/sections/admin/Order/SearchableDropdown";

const Transactions = () => {
  setTitle("Transaction");
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    data: null,
  });
  const [filters, setFilters] = useState({
    limit: 100,
    page: 1,
  });

  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState(null);
  const [selectFilterItem, setSelectFilterItem] = useState([]);

  const [selectedSearchData, setSelectedSearchData] = useState({
    category: [],
  });
  const [selectedTable, setSelectedTable] = useState(null);

  const { data: tables } = useQuery({
    queryKey: ["tables"],
    queryFn: async () => {
      const res = await tableList({ limit: 10000 });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs?.data;
    },
  });

  const { data, error, isLoading, updateData, isFetching, refetch } = useQuery({
    queryKey: ["transaction", filters, search, selectedSearchData?.category, selectFilterItem],
    queryFn: async () => {
      const res = await transactionList({
        ...filters,
        ...(selectedFilter === "status" && { status: selectFilterItem?.value }),
        ...(selectedFilter === "dateRange" && {
          startDate: dayjs(selectFilterItem?.value?.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(selectFilterItem?.value?.endDate).format("YYYY-MM-DD"),
        }),
        ...(search && { keyword: search }),
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
  });

  const tableHeadData = [
    {
      _id: 1,
      name: "Order Id",
      align: "left",
    },
    {
      _id: 2,
      name: "Bill Amount",
      align: "left",
    },
    {
      _id: 30,
      name: "Paid Amount",
      align: "left",
    },
    {
      _id: 3,
      name: "Tips Added",
      align: "left",
    },
    {
      _id: 3,
      name: "Created At",
      align: "left",
      isSearchFilter: true,
      onSearchFilter: (val) => {
        if (val?.startDate && val?.endDate) {
          setSelectedFilter("dateRange");
          setSelectFilterItem({
            value: {
              startDate: val.startDate,
              endDate: val.endDate,
            },
          });
          setFilters((prev) => ({ ...prev, page: 1 }));
        } else {
          setSelectedFilter(null);
          setSelectFilterItem(null);
        }
      },
      isOption: "range-picker",
    },
    {
      _id: 5,
      name: "Payment Method",
      align: "left",
    },
  ];
  // Table Body Data =====================>
  const tableData = data?.data?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 1,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.metadata?.orderId,
        },
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (<span className="font-bold text-blue-600">$ {item?.metadata?.billAmount.toFixed(2) || "-"}</span>),
        },
        {
          _id: 30,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: `$ ${item?.metadata?.totalAmount.toFixed(2)}`,
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.metadata?.tipAmount >= 0 ? `$ ${item?.metadata?.tipAmount}` : "-",
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <Tooltip
              position="right"
              message={
                <div className="text-[12px] whitespace-pre-wrap max-w-xs">
                  {item?.createdAt
                    ? `Transaction on ${dayjs(item.lastLogin).format("DD MMMM, YYYY")} at ${dayjs(item.createdAt).format("hh:mm A")}.`
                    : "No transaction."}
                </div>
              }
            >
              {item?.createdAt ? dayjs(item?.createdAt).format("DD MMM, YYYY • hh:mm A") : "No transaction"}
            </Tooltip>
          ),
        },
        {
          _id: 30,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data:
            item?.paymentGateway?.rawResponse?.payment_method_details?.card?.brand === "visa" ? (
              <span className="w-5 px-2 py-1 bg-yellow-100 border border-yellow-500 text-yellow-600 rounded-[100px]">Card</span>
            ) : (
              <span className="w-5 px-2 py-1 bg-purple-100 border border-purple-500 text-purple-600 rounded-[100px]">Manual</span>
            ),
        },
      ],
    };
  });

  const { mutate: deleteHandler, isPending: deletePending } = useMutation({
    mutationFn: async (payload) => {
      const res = await postDelete({
        ids: Array.isArray(payload) ? payload : [payload],
      });
      return {
        res,
        payload: Array.isArray(payload) ? payload : [payload],
      };
    },
    onSuccess: ({ res, payload }) => {
      if (res?.status === 200) {
        setDeleteModal({ isOpen: false, data: null });
        setSelectedItems([]);
        queryClient.setQueryData(["order", filters, search, selectFilterItem], (state) => {
          if (state) {
            let stateData = JSON.parse(JSON.stringify(state?.data));
            stateData = (stateData || [])?.filter((item) => !payload?.includes(item?._id));
            return { ...state, data: stateData };
          }
        });

        queryClient.invalidateQueries(["order", filters, search, selectFilterItem]);
      } else {
        toast.warning(res?.message || res?.error);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  async function postOrderingHandler(newData, oldData) {
    const ordering_data = newData?.map((item, i) => ({
      id: item?._id,
      ordering: i,
      categoryType: "blog",
    }));
    const res = await postOrdering({ ordering_data });
    if (res?.status !== 200) {
      queryClient.setQueryData(["blog", filters, search], (state) => {
        if (state) {
          return { ...state, data: oldData };
        }
      });
      toast.warning(res?.message || res?.error);
    }
  }
  function actionHandler(val) {
    switch (val) {
      case "blogDelete":
        setDeleteModal({
          isOpen: true,
          data: { _id: selectedItems },
        });
        break;

      default:
        break;
    }
  }
  const options = [
    {
      id: "blogDelete",
      icon: "fa-regular fa-trash-can",
      tooltip: "Blog Delete",
    },
  ];

  return (
    <div className="space-y-4 w-full flex flex-col pb-10">
      <div className="space-y-8">
        <div className="font-semibold text-lg xls:text-sm xl:text-3xl text-primary text-right xls:text-left">{`Transactions`}</div>

        <div className="flex items-center gap-3 flex-col xls:flex-row w-full">
          <TableControls
            limit={filters?.limit}
            totalDocs={data?.metadata?.totalDocs}
            onLimitChange={(val) => handleFilterChange("limit", val, setFilters)}
            onSearch={(val) => {
              setKeyword(val);
              handleFilterChange("page", 1, setFilters);
            }}
          />

          <div className="w-72">
            <SearchableDropdown
              options={
                tables?.map((t) => ({
                  label: `Table ${t.tableNumber} - ${t.tableName}`,
                  value: t._id,
                  tableNumber: t.tableNumber,
                  tableName: t.tableName,
                })) || []
              }
              placeholder="Filter by table no."
              selectedValue={
                selectedTable
                  ? {
                      label: `Table ${selectedTable.tableNumber} - ${selectedTable.tableName}`,
                      value: selectedTable._id,
                    }
                  : null
              }
              onSelect={(opt) => {
                setSelectedTable({
                  _id: opt.value,
                  tableNumber: opt.tableNumber,
                  tableName: opt.tableName,
                });
              }}
              searchKeys={["label", "tableNumber", "tableName"]}
              displaySearch={true}
              startIconClass="fa-solid fa-utensils"
              iconColor="red-900"
            />
          </div>
          {selectedItems?.length > 0 && (
            <div className="flex items-center gap-2">
              <ActionBar options={options} onChange={actionHandler} />
              <span className="flex items-center rounded-full bg-secondary text-surface-01 text-xs px-3 py-1 gap-2">
                {selectedItems?.length} Selected
                <button onClick={() => setSelectedItems([])} type="button" className="cursor-pointer">
                  <i className="fa-fw fa-light fa-xmark"></i>
                </button>
              </span>
            </div>
          )}
        </div>
      </div>

      <Table
        tableHeadData={tableHeadData}
        tableData={tableData}
        totalDataCount={data?.metadata?.totalDocs}
        isLoder={isLoading}
        pageCount={data?.metadata?.totalPages}
        currentPage={filters?.page}
        onPageChange={(val) => {
          handleFilterChange("page", val, setFilters);
          setSelectedItems([]);
        }}
        handleOrdering={(newData, oldData) => {
          postOrderingHandler(newData, oldData);
        }}
        originalData={[...(data?.data || [])]}
        setOriginalData={(data) =>
          queryClient.setQueryData(["blog", filters, search], (state) => {
            if (state) {
              return { ...state, data: data };
            }
          })
        }
        isFetching={isFetching}
        isOrderingDisabled={true}
        // selectable
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        tableActionDropdownContainer={"!w-48"}
        showingData={getPaginationRange(filters?.page, filters?.limit, data?.metadata?.totalDocs)}
      />
      <DeleteModal
        open={deleteModal?.isOpen}
        loading={deletePending}
        onClose={() => setDeleteModal((pre) => ({ data: null, isOpen: false }))}
        deleteHandler={() => {
          deleteHandler(deleteModal?.data?._id);
        }}
      />
    </div>
  );
};

export default Transactions;
